import { reactive, ref } from 'vue'
import type { FieldConfig, FieldValue, FormConfig, FormValues } from '@/forms/types'

function defaultValue(field: FieldConfig): FieldValue {
  switch (field.type) {
    case 'text':
    case 'email':
    case 'textarea':
    case 'radio':
    case 'select':
    case 'section': // no value; kept in the map so lookups never return undefined
      return ''
    case 'checkbox':
      return false
    case 'checkbox-group':
      return []
  }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function useFormState(config: FormConfig) {
  const allFields: FieldConfig[] = [...config.fields, config.consentField]
  const values = reactive<FormValues>(
    Object.fromEntries(allFields.map((f) => [f.id, defaultValue(f)])),
  )
  const errors = ref<Record<string, string>>({})

  function isVisible(field: FieldConfig): boolean {
    if (!field.dependsOn) return true
    return values[field.dependsOn.fieldId] === field.dependsOn.equals
  }

  function validate(): boolean {
    const next: Record<string, string> = {}
    for (const field of allFields) {
      if (!isVisible(field)) continue
      const v = values[field.id]

      if (field.required) {
        if (field.type === 'checkbox') {
          if (v !== true) next[field.id] = 'required'
        } else if (Array.isArray(v)) {
          if (v.length === 0) next[field.id] = 'required'
        } else if (typeof v !== 'string' || v.trim() === '') {
          next[field.id] = 'required'
        }
      }
      if (field.type === 'email' && typeof v === 'string' && v.trim() !== '') {
        if (!EMAIL_RE.test(v)) next[field.id] = 'invalid_email'
      }
    }
    errors.value = next
    return Object.keys(next).length === 0
  }

  function reset() {
    for (const f of allFields) values[f.id] = defaultValue(f)
    errors.value = {}
  }

  return { values, errors, isVisible, validate, reset, allFields }
}
